import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipIconWithHover} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipIconWithHover';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  getAnalyticsReportErrors,
  getAnalyticsReportHasStarted,
  getAnalyticsReportLastUpdated,
  getAnalyticsReportProgress,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {maybeRenderLoader} from '../../reportHeader/titleRow/reportHeaderLoaderHelper';

/*
 * Style.
 */

const TextFlex = styled.div`
  display: flex;
  align-items: baseline;
`;

const StyledFlex = styled.div`
  ${flexCenter};
`;

const StyledTitleRowWrapper = styled(StyledFlex)`
  justify-content: space-between;
  margin-bottom: 24px;
`;

const StyledReportTitle = styled.div`
  margin-right: 8px;

  color: ${(p) => p.theme.greys.shade90};

  // Not supported in fontSizes currently
  font-size: 24px;
  font-weight: ${fontWeights.black};
`;

const StyledTooltipWrapperDiv = styled.div`
  display: inline-flex;
  padding: 6px 8px;
  align-items: center;
  gap: 10px;
  line-height: ${fontSizes.veryLarge};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

const StyledLoaderWrapperDiv = styled.div`
  margin-left: 16px;
`;

/*
 * Component.
 */

// Similar to ReportHeaderTitleRow, with changes made for Chatbots implementation
export const ChatbotReportHeaderTitleRow: FC = () => {
  const reportProgress = useAppSelector(getAnalyticsReportProgress);
  const reportHasStarted = useAppSelector(getAnalyticsReportHasStarted);
  const reportLastUpdated = useAppSelector(getAnalyticsReportLastUpdated);
  const errors = useAppSelector(getAnalyticsReportErrors);

  return (
    <StyledTitleRowWrapper>
      <TextFlex>
        <StyledReportTitle>
          <Trans id="analytics-automation-reports-chatbots-header">Chatbots</Trans>
        </StyledReportTitle>

        <TooltipCoordinator
          placement="bottom"
          maxWidth={244}
          customMargin={8}
          render={renderChatbotsHeaderTooltipContent}
        >
          <TooltipIconWithHover />
        </TooltipCoordinator>

        <StyledLoaderWrapperDiv>
          {maybeRenderLoader(errors, reportProgress, reportHasStarted, reportLastUpdated)}
        </StyledLoaderWrapperDiv>
      </TextFlex>
    </StyledTitleRowWrapper>
  );
};

/*
 * Helpers.
 */

function renderChatbotsHeaderTooltipContent() {
  return (
    <StyledTooltipWrapperDiv>
      <Trans id="analytics-automation-reports-chatbots-header-tooltip-content">
        Understand how Front Chat chatbots are performing. This data pertains only to chatbot interactions
        prior to the creation of the chat conversation. To measure how your team interacts with chat
        conversations, use the other reports.
      </Trans>
    </StyledTooltipWrapperDiv>
  );
}
